import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Save,
  Upload,
  User,
  MapPin,
  Calendar,
  DollarSign,
  GraduationCap,
  FileText,
  Camera,
  AlertCircle,
  CheckCircle,
  Search
} from 'lucide-react';
import { useCreateChildMutation } from '../../store/slices/apiSlice';
import toast from 'react-hot-toast';
import { API_BASE_URL } from '../../config/api';

interface ChildFormData {
  name: string;
  age: number;
  location: string;
  educationStage: string;
  bio: string;
  countryCode: string;
  requiredFunding: {
    weekly: number;
    monthly: number;
    yearly: number;
  };
  photo: File | null;
  verificationNotes: string;
}

interface LocationSuggestion {
  description: string;
  place_id: string;
  structured_formatting: {
    main_text: string;
    secondary_text: string;
  };
  terms: Array<{
    offset: number;
    value: string;
  }>;
}

const CreateChild: React.FC = () => {
  const navigate = useNavigate();
  const [createChild, { isLoading: isSubmitting }] = useCreateChildMutation();

  const [formData, setFormData] = useState<ChildFormData>({
    name: '',
    age: 0,
    location: '',
    educationStage: '',
    bio: '',
    countryCode: 'NG',
    requiredFunding: {
      weekly: 0,
      monthly: 0,
      yearly: 0
    },
    photo: null,
    verificationNotes: ''
  });

  const [errors, setErrors] = useState<Partial<ChildFormData>>({});
  const [previewImage, setPreviewImage] = useState<string | null>(null);

  // Location search states
  const [locationSearch, setLocationSearch] = useState('');
  const [showLocationSuggestions, setShowLocationSuggestions] = useState(false);
  const [locationSuggestions, setLocationSuggestions] = useState<LocationSuggestion[]>([]);
  const [isSearchingLocation, setIsSearchingLocation] = useState(false);

  const educationStages = [
    'Nursery',
    'Primary 1',
    'Primary 2',
    'Primary 3',
    'Primary 4',
    'Primary 5',
    'Primary 6',
    'JSS 1',
    'JSS 2',
    'JSS 3',
    'SSS 1',
    'SSS 2',
    'SSS 3',
    'High School',
    'College',
    'University'
  ];

  const countryCodes = [
    { code: 'NG', name: 'Nigeria', currency: 'NGN' },
    { code: 'US', name: 'United States', currency: 'USD' },
    { code: 'GB', name: 'United Kingdom', currency: 'GBP' },
    { code: 'IN', name: 'India', currency: 'INR' },
    { code: 'CA', name: 'Canada', currency: 'CAD' },
    { code: 'AU', name: 'Australia', currency: 'AUD' },
    { code: 'DE', name: 'Germany', currency: 'EUR' },
    { code: 'FR', name: 'France', currency: 'EUR' },
    { code: 'IT', name: 'Italy', currency: 'EUR' },
    { code: 'ES', name: 'Spain', currency: 'EUR' },
    { code: 'NL', name: 'Netherlands', currency: 'EUR' },
    { code: 'BE', name: 'Belgium', currency: 'EUR' },
    { code: 'AT', name: 'Austria', currency: 'EUR' },
    { code: 'CH', name: 'Switzerland', currency: 'CHF' },
    { code: 'SE', name: 'Sweden', currency: 'SEK' },
    { code: 'NO', name: 'Norway', currency: 'NOK' },
    { code: 'DK', name: 'Denmark', currency: 'DKK' },
    { code: 'FI', name: 'Finland', currency: 'EUR' },
    { code: 'IE', name: 'Ireland', currency: 'EUR' },
    { code: 'PT', name: 'Portugal', currency: 'EUR' },
    { code: 'GR', name: 'Greece', currency: 'EUR' },
    { code: 'PL', name: 'Poland', currency: 'PLN' },
    { code: 'CZ', name: 'Czech Republic', currency: 'CZK' },
    { code: 'HU', name: 'Hungary', currency: 'HUF' },
    { code: 'RO', name: 'Romania', currency: 'RON' },
    { code: 'BG', name: 'Bulgaria', currency: 'BGN' },
    { code: 'HR', name: 'Croatia', currency: 'EUR' },
    { code: 'SI', name: 'Slovenia', currency: 'EUR' },
    { code: 'SK', name: 'Slovakia', currency: 'EUR' },
    { code: 'LT', name: 'Lithuania', currency: 'EUR' },
    { code: 'LV', name: 'Latvia', currency: 'EUR' },
    { code: 'EE', name: 'Estonia', currency: 'EUR' },
    { code: 'CY', name: 'Cyprus', currency: 'EUR' },
    { code: 'MT', name: 'Malta', currency: 'EUR' },
    { code: 'LU', name: 'Luxembourg', currency: 'EUR' },
    { code: 'JP', name: 'Japan', currency: 'JPY' },
    { code: 'KR', name: 'South Korea', currency: 'KRW' },
    { code: 'CN', name: 'China', currency: 'CNY' },
    { code: 'HK', name: 'Hong Kong', currency: 'HKD' },
    { code: 'SG', name: 'Singapore', currency: 'SGD' },
    { code: 'MY', name: 'Malaysia', currency: 'MYR' },
    { code: 'TH', name: 'Thailand', currency: 'THB' },
    { code: 'ID', name: 'Indonesia', currency: 'IDR' },
    { code: 'PH', name: 'Philippines', currency: 'PHP' },
    { code: 'VN', name: 'Vietnam', currency: 'VND' },
    { code: 'ZA', name: 'South Africa', currency: 'ZAR' },
    { code: 'EG', name: 'Egypt', currency: 'EGP' },
    { code: 'KE', name: 'Kenya', currency: 'KES' },
    { code: 'GH', name: 'Ghana', currency: 'GHS' },
    { code: 'MA', name: 'Morocco', currency: 'MAD' },
    { code: 'TN', name: 'Tunisia', currency: 'TND' },
    { code: 'DZ', name: 'Algeria', currency: 'DZD' },
    { code: 'LY', name: 'Libya', currency: 'LYD' },
    { code: 'SD', name: 'Sudan', currency: 'SDG' },
    { code: 'ET', name: 'Ethiopia', currency: 'ETB' },
    { code: 'UG', name: 'Uganda', currency: 'UGX' },
    { code: 'TZ', name: 'Tanzania', currency: 'TZS' },
    { code: 'RW', name: 'Rwanda', currency: 'RWF' },
    { code: 'BF', name: 'Burkina Faso', currency: 'XOF' },
    { code: 'ML', name: 'Mali', currency: 'XOF' },
    { code: 'SN', name: 'Senegal', currency: 'XOF' },
    { code: 'CI', name: 'Ivory Coast', currency: 'XOF' },
    { code: 'NE', name: 'Niger', currency: 'XOF' },
    { code: 'TD', name: 'Chad', currency: 'XAF' },
    { code: 'CM', name: 'Cameroon', currency: 'XAF' },
    { code: 'CF', name: 'Central African Republic', currency: 'XAF' },
    { code: 'CG', name: 'Congo', currency: 'XAF' },
    { code: 'CD', name: 'Democratic Republic of Congo', currency: 'CDF' },
    { code: 'GA', name: 'Gabon', currency: 'XAF' },
    { code: 'GQ', name: 'Equatorial Guinea', currency: 'XAF' },
    { code: 'ST', name: 'São Tomé and Príncipe', currency: 'STD' },
    { code: 'AO', name: 'Angola', currency: 'AOA' },
    { code: 'ZM', name: 'Zambia', currency: 'ZMW' },
    { code: 'ZW', name: 'Zimbabwe', currency: 'ZWL' },
    { code: 'BW', name: 'Botswana', currency: 'BWP' },
    { code: 'NA', name: 'Namibia', currency: 'NAD' },
    { code: 'SZ', name: 'Eswatini', currency: 'SZL' },
    { code: 'LS', name: 'Lesotho', currency: 'LSL' },
    { code: 'MW', name: 'Malawi', currency: 'MWK' },
    { code: 'MZ', name: 'Mozambique', currency: 'MZN' },
    { code: 'MG', name: 'Madagascar', currency: 'MGA' },
    { code: 'MU', name: 'Mauritius', currency: 'MUR' },
    { code: 'SC', name: 'Seychelles', currency: 'SCR' },
    { code: 'KM', name: 'Comoros', currency: 'KMF' },
    { code: 'DJ', name: 'Djibouti', currency: 'DJF' },
    { code: 'SO', name: 'Somalia', currency: 'SOS' },
    { code: 'ER', name: 'Eritrea', currency: 'ERN' },
    { code: 'SS', name: 'South Sudan', currency: 'SSP' },
    { code: 'BI', name: 'Burundi', currency: 'BIF' },
    { code: 'SL', name: 'Sierra Leone', currency: 'SLE' },
    { code: 'LR', name: 'Liberia', currency: 'LRD' },
    { code: 'GN', name: 'Guinea', currency: 'GNF' },
    { code: 'GW', name: 'Guinea-Bissau', currency: 'XOF' },
    { code: 'GM', name: 'Gambia', currency: 'GMD' },
    { code: 'CV', name: 'Cape Verde', currency: 'CVE' },
    { code: 'MR', name: 'Mauritania', currency: 'MRU' },
    { code: 'TG', name: 'Togo', currency: 'XOF' },
    { code: 'BJ', name: 'Benin', currency: 'XOF' }
  ];

  const handleInputChange = (field: keyof ChildFormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  // Get currency symbol based on selected country
  const getCurrencySymbol = (countryCode: string) => {
    const country = countryCodes.find(c => c.code === countryCode);
    if (!country) return '₦'; // Default to Naira

    // Map currency codes to symbols
    const currencySymbols: { [key: string]: string } = {
      'NGN': '₦',
      'USD': '$',
      'GBP': '£',
      'INR': '₹',
      'CAD': 'C$',
      'AUD': 'A$',
      'EUR': '€',
      'CHF': 'CHF',
      'SEK': 'kr',
      'NOK': 'kr',
      'DKK': 'kr',
      'JPY': '¥',
      'KRW': '₩',
      'CNY': '¥',
      'HKD': 'HK$',
      'SGD': 'S$',
      'MYR': 'RM',
      'THB': '฿',
      'IDR': 'Rp',
      'PHP': '₱',
      'VND': '₫',
      'ZAR': 'R',
      'EGP': 'E£',
      'KES': 'KSh',
      'GHS': 'GH₵',
      'MAD': 'MAD',
      'TND': 'DT',
      'DZD': 'DA',
      'LYD': 'LD',
      'SDG': 'SDG',
      'ETB': 'Br',
      'UGX': 'USh',
      'TZS': 'TSh',
      'RWF': 'RF',
      'XOF': 'CFA',
      'XAF': 'FCFA',
      'CDF': 'FC',
      'STD': 'Db',
      'AOA': 'Kz',
      'ZMW': 'ZK',
      'ZWL': 'Z$',
      'BWP': 'P',
      'NAD': 'N$',
      'SZL': 'L',
      'LSL': 'L',
      'MWK': 'MK',
      'MZN': 'MT',
      'MGA': 'Ar',
      'MUR': '₨',
      'SCR': '₨',
      'KMF': 'CF',
      'DJF': 'Fdj',
      'SOS': 'S',
      'ERN': 'Nfk',
      'SSP': 'SS£',
      'BIF': 'FBu',
      'SLE': 'Le',
      'LRD': 'L$',
      'GNF': 'FG',
      'GMD': 'D',
      'CVE': '$',
      'MRU': 'UM'
    };

    return currencySymbols[country.currency] || country.currency;
  };

  // Search location using the API
  const searchLocation = async (query: string) => {
    if (!query.trim()) {
      setLocationSuggestions([]);
      setShowLocationSuggestions(false);
      return;
    }

    // Check if user is authenticated
    const token = localStorage.getItem('diasporawise_admin_token');
    if (!token) {
      toast.error('You are not authenticated. Please login again.');
      setLocationSuggestions([]);
      setShowLocationSuggestions(false);
      return;
    }

    // Validate token format
    if (token.length < 10) {
      toast.error('Invalid authentication token. Please login again.');
      setLocationSuggestions([]);
      setShowLocationSuggestions(false);
      return;
    }

    // Check if token looks like a JWT (has dots)
    if (!token.includes('.')) {
      toast.error('Invalid token format. Please login again.');
      setLocationSuggestions([]);
      setShowLocationSuggestions(false);
      return;
    }

    console.log('Searching location with token:', token.substring(0, 20) + '...');

    setIsSearchingLocation(true);
    try {
      const response = await fetch(`${API_BASE_URL}/admin/search-location?input=${encodeURIComponent(query)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();

      if (data.success && data.data) {
        setLocationSuggestions(data.data);
        setShowLocationSuggestions(true);
      } else {
        setLocationSuggestions([]);
        setShowLocationSuggestions(false);
        if (data.message) {
          toast.error(`Location search failed: ${data.message}`);
        }
      }
    } catch (error: any) {
      console.error('Location search error:', error);
      setLocationSuggestions([]);
      setShowLocationSuggestions(false);

      // Handle specific error cases
      if (error?.status === 401) {
        toast.error('Authentication failed. Please login again.');
      } else if (error?.status === 500) {
        toast.error('Server error. Please try again later.');
      } else {
        toast.error('Location search failed. Please try again.');
      }
    } finally {
      setIsSearchingLocation(false);
    }
  };

  const handleLocationInputChange = (value: string) => {
    setLocationSearch(value);
    setFormData(prev => ({
      ...prev,
      location: value
    }));

    // Clear error when user starts typing
    if (errors.location) {
      setErrors(prev => ({
        ...prev,
        location: undefined
      }));
    }

    // Search for locations
    const timeoutId = setTimeout(() => {
      searchLocation(value);
    }, 300);

    return () => clearTimeout(timeoutId);
  };

  const handleLocationSelect = (suggestion: LocationSuggestion) => {
    setLocationSearch(suggestion.description);
    
    // Auto-detect country code from the location description
    const desc = suggestion.description.toLowerCase();
    let detectedCountryCode = formData.countryCode;
    
    if (desc.includes('india') || desc.includes('in')) detectedCountryCode = 'IN';
    else if (desc.includes('nigeria') || desc.includes('ng')) detectedCountryCode = 'NG';
    else if (desc.includes('united states') || desc.includes('usa') || desc.includes('us')) detectedCountryCode = 'US';
    else if (desc.includes('united kingdom') || desc.includes('uk') || desc.includes('gb')) detectedCountryCode = 'GB';
    
    setFormData(prev => ({
      ...prev,
      location: suggestion.description,
      countryCode: detectedCountryCode
    }));
    setShowLocationSuggestions(false);
    setLocationSuggestions([]);
  };

  const handleFundingChange = (field: keyof ChildFormData['requiredFunding'], value: number) => {
    setFormData(prev => ({
      ...prev,
      requiredFunding: {
        ...prev.requiredFunding,
        [field]: value
      }
    }));
  };

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setFormData(prev => ({
        ...prev,
        photo: file
      }));

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewImage(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<ChildFormData> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.age || formData.age <= 0) {
      newErrors.age = 'Age is required and must be greater than 0';
    }

    if (!formData.location.trim()) {
      newErrors.location = 'Location is required';
    }

    if (!formData.educationStage) {
      newErrors.educationStage = 'Education stage is required';
    }

    if (!formData.bio.trim()) {
      newErrors.bio = 'Bio is required';
    }

    if (!formData.photo) {
      newErrors.photo = 'Profile photo is required';
    }

    if (!formData.verificationNotes.trim()) {
      newErrors.verificationNotes = 'Verification notes are required';
    }

    if (formData.requiredFunding.weekly <= 0) {
      newErrors.requiredFunding = { ...newErrors.requiredFunding, weekly: 'Weekly funding must be greater than 0' };
    }

    if (formData.requiredFunding.monthly <= 0) {
      newErrors.requiredFunding = { ...newErrors.requiredFunding, monthly: 'Monthly funding must be greater than 0' };
    }

    if (formData.requiredFunding.yearly <= 0) {
      newErrors.requiredFunding = { ...newErrors.requiredFunding, yearly: 'Yearly funding must be greater than 0' };
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      const response = await createChild({
        name: formData.name,
        age: formData.age,
        location: formData.location,
        education_stage: formData.educationStage,
        bio: formData.bio,
        currency_code: countryCodes.find(c => c.code === formData.countryCode)?.currency || 'NGN',
        country_code: formData.countryCode,
        weekly_funding_required: formData.requiredFunding.weekly,
        monthly_funding_required: formData.requiredFunding.monthly,
        yearly_funding_required: formData.requiredFunding.yearly,
        photo: formData.photo,
        verification_notes: formData.verificationNotes
      }).unwrap();

      toast.success('Child created successfully!');
      navigate('/admin/child-sponsorship');
    } catch (error: any) {
      console.error('Error creating child:', error);
      toast.error(error?.data?.message || 'Failed to create child');
    }
  };

  const formatCurrency = (amount: number) => {
    const country = countryCodes.find(c => c.code === formData.countryCode);
    const currency = country?.currency || 'NGN';

    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
    }).format(amount).replace(/[A-Z]{3}/, getCurrencySymbol(formData.countryCode));
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/admin/child-sponsorship')}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Add New Child</h1>
                <p className="text-gray-600">Create a new child profile for sponsorship</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="px-6 py-6">
        <div className="max-w-4xl mx-auto">
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Basic Information */}
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                  <User className="w-5 h-5 mr-2" />
                  Basic Information
                </h2>
              </div>
              <div className="p-6 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.name ? 'border-red-300' : 'border-gray-300'
                        }`}
                      placeholder="Enter child's full name"
                    />
                    {errors.name && (
                      <p className="mt-1 text-sm text-red-600 flex items-center">
                        <AlertCircle className="w-4 h-4 mr-1" />
                        {errors.name}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Age *
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={formData.age || ''}
                      onChange={(e) => handleInputChange('age', parseInt(e.target.value) || 0)}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.age ? 'border-red-300' : 'border-gray-300'
                        }`}
                      placeholder="Enter age"
                    />
                    {errors.age && (
                      <p className="mt-1 text-sm text-red-600 flex items-center">
                        <AlertCircle className="w-4 h-4 mr-1" />
                        {errors.age}
                      </p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Location *
                    </label>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                      <input
                        type="text"
                        value={locationSearch}
                        onChange={(e) => handleLocationInputChange(e.target.value)}
                        className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.location ? 'border-red-300' : 'border-gray-300'
                          }`}
                        placeholder="City, State, Country"
                        autoComplete="off"
                      />
                      {isSearchingLocation && (
                        <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                        </div>
                      )}
                    </div>

                    {/* Location Suggestions Dropdown */}
                    {showLocationSuggestions && locationSuggestions.length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                        {locationSuggestions.map((suggestion, index) => (
                          <div
                            key={suggestion.place_id}
                            onClick={() => handleLocationSelect(suggestion)}
                            className="px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                          >
                            <div className="flex items-center">
                              <MapPin className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 truncate">
                                  {suggestion.structured_formatting.main_text}
                                </p>
                                {suggestion.structured_formatting.secondary_text && (
                                  <p className="text-sm text-gray-500 truncate">
                                    {suggestion.structured_formatting.secondary_text}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {errors.location && (
                      <p className="mt-1 text-sm text-red-600 flex items-center">
                        <AlertCircle className="w-4 h-4 mr-1" />
                        {errors.location}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Education Stage *
                    </label>
                    <div className="relative">
                      <GraduationCap className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                      <select
                        value={formData.educationStage}
                        onChange={(e) => handleInputChange('educationStage', e.target.value)}
                        className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.educationStage ? 'border-red-300' : 'border-gray-300'
                          }`}
                      >
                        <option value="">Select education stage</option>
                        {educationStages.map((stage) => (
                          <option key={stage} value={stage}>
                            {stage}
                          </option>
                        ))}
                      </select>
                    </div>
                    {errors.educationStage && (
                      <p className="mt-1 text-sm text-red-600 flex items-center">
                        <AlertCircle className="w-4 h-4 mr-1" />
                        {errors.educationStage}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Country *
                    </label>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                      <select
                        value={formData.countryCode}
                        onChange={(e) => handleInputChange('countryCode', e.target.value)}
                        className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.countryCode ? 'border-red-300' : 'border-gray-300'
                          }`}
                      >
                        {countryCodes.map((country) => (
                          <option key={country.code} value={country.code}>
                            {country.name} ({country.code}) - {country.currency}
                          </option>
                        ))}
                      </select>
                    </div>
                    {errors.countryCode && (
                      <p className="mt-1 text-sm text-red-600 flex items-center">
                        <AlertCircle className="w-4 h-4 mr-1" />
                        {errors.countryCode}
                      </p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Bio *
                  </label>
                  <textarea
                    value={formData.bio}
                    onChange={(e) => handleInputChange('bio', e.target.value)}
                    rows={4}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.bio ? 'border-red-300' : 'border-gray-300'
                      }`}
                    placeholder="Tell us about the child's interests, dreams, and background..."
                  />
                  {errors.bio && (
                    <p className="mt-1 text-sm text-red-600 flex items-center">
                      <AlertCircle className="w-4 h-4 mr-1" />
                      {errors.bio}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Photo Upload */}
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                  <Camera className="w-5 h-5 mr-2" />
                  Profile Photo
                </h2>
              </div>
              <div className="p-6">
                <div className="flex items-center space-x-6">
                  <div className="w-24 h-24 bg-gray-200 rounded-full flex items-center justify-center overflow-hidden">
                    {previewImage ? (
                      <img src={previewImage} alt="Preview" className="w-full h-full object-cover" />
                    ) : (
                      <User className="w-8 h-8 text-gray-400" />
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Upload Photo *
                    </label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handlePhotoChange}
                      className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-green-50 file:text-green-700 hover:file:bg-green-100"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      JPG, PNG or GIF. Max size 5MB.
                    </p>
                    {errors.photo && (
                      <p className="mt-1 text-sm text-red-600 flex items-center">
                        <AlertCircle className="w-4 h-4 mr-1" />
                        {errors.photo}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Funding Requirements */}
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                  <DollarSign className="w-5 h-5 mr-2" />
                  Funding Requirements
                </h2>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Weekly Amount *
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">{getCurrencySymbol(formData.countryCode)}</span>
                      <input
                        type="number"
                        min="0"
                        step="1"
                        value={formData.requiredFunding.weekly || ''}
                        onChange={(e) => handleFundingChange('weekly', parseInt(e.target.value) || 0)}
                        className={`w-full pl-8 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.requiredFunding?.weekly ? 'border-red-300' : 'border-gray-300'
                          }`}
                        placeholder="0"
                      />
                    </div>
                    {errors.requiredFunding?.weekly && (
                      <p className="mt-1 text-sm text-red-600 flex items-center">
                        <AlertCircle className="w-4 h-4 mr-1" />
                        {errors.requiredFunding.weekly}
                      </p>
                    )}
                    <p className="text-xs text-gray-500 mt-1">
                      {formatCurrency(formData.requiredFunding.weekly)}
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Monthly Amount *
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">{getCurrencySymbol(formData.countryCode)}</span>
                      <input
                        type="number"
                        min="0"
                        step="1"
                        value={formData.requiredFunding.monthly || ''}
                        onChange={(e) => handleFundingChange('monthly', parseInt(e.target.value) || 0)}
                        className={`w-full pl-8 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.requiredFunding?.monthly ? 'border-red-300' : 'border-gray-300'
                          }`}
                        placeholder="0"
                      />
                    </div>
                    {errors.requiredFunding?.monthly && (
                      <p className="mt-1 text-sm text-red-600 flex items-center">
                        <AlertCircle className="w-4 h-4 mr-1" />
                        {errors.requiredFunding.monthly}
                      </p>
                    )}
                    <p className="text-xs text-gray-500 mt-1">
                      {formatCurrency(formData.requiredFunding.monthly)}
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Yearly Amount *
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">{getCurrencySymbol(formData.countryCode)}</span>
                      <input
                        type="number"
                        min="0"
                        step="1"
                        value={formData.requiredFunding.yearly || ''}
                        onChange={(e) => handleFundingChange('yearly', parseInt(e.target.value) || 0)}
                        className={`w-full pl-8 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.requiredFunding?.yearly ? 'border-red-300' : 'border-gray-300'
                          }`}
                        placeholder="0"
                      />
                    </div>
                    {errors.requiredFunding?.yearly && (
                      <p className="mt-1 text-sm text-red-600 flex items-center">
                        <AlertCircle className="w-4 h-4 mr-1" />
                        {errors.requiredFunding.yearly}
                      </p>
                    )}
                    <p className="text-xs text-gray-500 mt-1">
                      {formatCurrency(formData.requiredFunding.yearly)}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Verification Notes */}
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                  <CheckCircle className="w-5 h-5 mr-2" />
                  Verification Notes
                </h2>
              </div>
              <div className="p-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Verification Notes *
                  </label>
                  <textarea
                    value={formData.verificationNotes}
                    onChange={(e) => handleInputChange('verificationNotes', e.target.value)}
                    rows={3}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.verificationNotes ? 'border-red-300' : 'border-gray-300'
                      }`}
                    placeholder="Add any verification notes or requirements..."
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    Add notes about verification requirements or status for this child.
                  </p>
                  {errors.verificationNotes && (
                    <p className="mt-1 text-sm text-red-600 flex items-center">
                      <AlertCircle className="w-4 h-4 mr-1" />
                      {errors.verificationNotes}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Submit Buttons */}
            <div className="flex items-center justify-end space-x-4">
              <button
                type="button"
                onClick={() => navigate('/admin/child-sponsorship')}
                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex items-center px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Creating...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Create Child
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateChild;
